<style lang="less" scoped>
@import "/styles/SiteColors.less";
button.close {
    position: fixed;
    top: 30px;
    right: 30px;
    color: #fff;
    font-size: 2em;
    line-height: 0;
    opacity: 1;
    text-shadow: none;
}
.modal-content {
    background: transparent;
    border: unset;

    .nav-search-group {
        ::v-deep(label.search-label) {
            font-size: 1.5em;
            color: #aaa;
            margin-bottom: 0;
            -webkit-transition: all 0.25s;
            transition: all 0.25s;
            display: block;
        }

        ::v-deep(input#navSearch.accessible-search-input) {
            background: none;
            height: 40px;
            color: #ddd;
            border: none;
            padding: 10px;
            border-bottom: 1px solid #aaa;
            box-shadow: none;
            padding-right: 1.75rem;

            /**
            * Remove default clear icon of search input type.
            * https://stackoverflow.com/a/77190241
            */
            appearance: textfield;
            outline-offset: -2px; // Fix outline style in Safari

            &::-webkit-search-decoration,
            &::-webkit-search-cancel-button,
            &::-webkit-search-results-button,
            &::-webkit-search-results-decoration {
                -webkit-appearance: none;
            }

            &:focus {
                border-color: @link-color;
                border-bottom-width: 2px;
            }
        }

        ::v-deep(.clear) {
            position: absolute;
            right: 0;
            color: white;

            &:focus {
                outline-offset: 0;
                outline-color: white;
            }
        }
    }
}
@media (max-width: 768px) {
    button.close {
        font-size: 1em;
    }
    .modal-body {
        margin-top: 0.5rem;
        padding: 0.25rem;
    }
}
</style>
<template>
    <div class="modal-content" ref="container">
        <form id="navSearchForm" asp-antiforgery="false" @submit.prevent="searchTextSet(searchText2)" role="search">
            <div class="modal-header clearfix">
                <h3 class="modal-title sr-only">Search</h3>
                <button type="button" class="close icon icon-close" @click="close" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <div class="form-group form-group-lg nav-search-group">
                    <AccessibleSearchInput :model-value="searchText2" id="navSearch" class="align-items-center" input-class="form-control rounded-0" placeholder="" label="Search" label-class="search-label" :label-outside-input-group="true" autocomplete="off" :autofocus="true" @update:model-value="setSearchText2" @clear="clearSearch" />
                    <Promised :promise="searchResultsPromise" v-slot:combined="{ isPending, data: searchData, error }" :pendingDelay="0" v-if="searchResultsPromise != null">
                        <div class="nav-search-results w-100" id="searchResults" v-show="isPending || searchData != null || error != null">
                            <div class="nav-search-results-inner">
                                <span class="nav-search-results-caption">
                                    Search results
                                    {{ searchRootTenant ? "for all sites" : "for this site" }}
                                </span>
                                <div style="width: 100%">
                                    <div v-show="isPending" style="display: none; width: 100%">
                                        <LoadingWheel :width="40" />
                                    </div>
                                    <div v-show="!isPending">
                                        <div v-show="searchData && !searchData.length" class="no_results_found" style="display: none; width: 100%">No results were found.</div>
                                        <div v-show="searchData && searchData.length" style="width: 100%">
                                            <div class="result" v-for="result in searchData">
                                                <Promised :pending-delay="null" :promise="searchResultUrl(result)" v-slot="url">
                                                    <a @click.prevent="gotoSearchResult(result)" :href="url">{{ result.label }}</a>
                                                </Promised>
                                                <Promised :pending-delay="null" :promise="result.tenantDisplay" v-slot="tenantDisplay" v-if="result.tenant != data.tenantData?.tenantCode">
                                                    &nbsp;
                                                    <span class="badge badge-light">{{ tenantDisplay }}</span>
                                                </Promised>
                                                <p style="margin-bottom: 5px">{{ result.description }}</p>
                                            </div>
                                        </div>
                                        <div v-show="searchData">
                                            <hr />
                                            <button class="btn btn-link" type="button" @click="searchRootTenant = !searchRootTenant">
                                                Search
                                                {{ searchRootTenant ? "in this site" : "all sites" }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Promised>
                </div>
            </div>
        </form>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, inject, toRef, SetupContext, ComputedRef } from "vue";
import { watchDebounced } from "@vueuse/core";
import { useMainStore, TenantData } from "./mainStore";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";
import { Promised } from "@/lib/Promised";
import LoadingWheel from "./lib/LoadingWheel.3.vue";
import AccessibleSearchInput from "@/lib/AccessibleSearchInput.3.vue";
import type { SearchResults } from "@cs/SearchResults";
import type { UserData } from "@cs/UserData";
interface Props extends DialogProps {
    data: { userData: UserData | null; tenantData: TenantData | null };
    searchText: string | null;
    updateSearchText: (text: string | null) => void;
}
const SEARCH_DEBOUNCE_MS = 300;
export default defineComponent({
    props: ["data", "searchText", "updateSearchText", "triggerElement"],
    emits: ["close"],
    components: { Promised, LoadingWheel, AccessibleSearchInput },
    setup(props: Props, context: SetupContext) {
        const mainStore = useMainStore();
        const changePage = inject(changePageInjectKey)!;

        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);

        //States
        const searchText2 = toRef(props.searchText);
        const searchTextDebounced = ref(null as string | null);
        const searchRootTenant = ref(false as boolean);

        //Computed
        const searchResultsPromise = computed(async () => {
            if (searchTextDebounced.value == null || /^\s*$/.test(searchTextDebounced.value)) {
                return null;
            }
            const searchTextDebouncedVal = searchTextDebounced.value;
            const searchFromRoot = searchRootTenant.value;
            const searchUrl = await mainStore.getUrl("appShellSearch", {
                searchText: searchTextDebouncedVal,
                searchFromRoot: searchFromRoot,
            });
            const searchResultJsonPromise = fetch(searchUrl).then(i => i.json());
            mainStore.setLiveRegion(
                searchResultJsonPromise.then(search => {
                    return search.Results.length == 0 ? "No results were found." : `${search.Results.length} ${search.Results.length > 1 ? "results" : "result"} found.`;
                }),
            );
            const searchResultJson = (await searchResultJsonPromise) as SearchResults;
            const tenantDisplayLabel = mainStore.getTenantDisplayLabel(searchResultJson.Results.map(i => i.FormTenant));
            return searchResultJson.Results.map(v => ({
                tenant: v.FormTenant,
                tenantDisplay: tenantDisplayLabel[v.FormTenant],
                label: v.Label,
                description: v.Description,
                urlSlug: v.FormUrlSlug,
                formName: v.FormName,
                formVersion: v.FormVersion,
                formHash: v.FormHash,
                openInNewTab: v.OpenInNewTab,
            }));
        });

        //Methods
        const searchTextSet = (searchTextValue: string | null) => {
            searchTextDebounced.value = searchTextValue;
        };
        const setSearchText2 = (searchTextValue: string | null) => {
            searchText2.value = searchTextValue;
        };
        const clearSearch = () => {
            searchText2.value = null;
            searchTextSet(null);
        };
        const close = () => {
            context.emit("close");
            props.updateSearchText(searchText2.value);
        };
        const gotoSearchResult = (result: SearchResult) => {
            changePage(
                {
                    page: "Page",
                    settings: {
                        areaCode: result.tenant,
                        urlSlug: result.urlSlug,
                        formName: result.formName,
                        formVersion: result.formVersion,
                        formHash: result.formHash,
                        page: 0,
                    },
                },
                false,
                false,
                !!result.openInNewTab,
            );
            close();
        };
        type extractComputedRef<Type> = Type extends ComputedRef<Promise<infer T>> ? T : Type extends ComputedRef<infer T2> ? T2 : never;
        type SearchResult = NonNullable<extractComputedRef<typeof searchResultsPromise>>[0];
        const searchResultUrl = (result: SearchResult) => {
            if (result.urlSlug) {
                return mainStore.getUrl("formGotoSlug", {
                    areaCode: result.tenant,
                    urlSlug: result.urlSlug,
                    page: 0,
                });
            } else {
                return mainStore.getUrl("formGoto", {
                    areaCode: result.tenant,
                    formName: result.formName,
                    formVersion: result.formVersion,
                    formHash: result.formHash,
                    page: 0,
                });
            }
        };

        watchDebounced(
            searchText2,
            newValue => {
                searchTextSet(newValue);
            },
            { debounce: SEARCH_DEBOUNCE_MS, immediate: true },
        );

        return {
            container: containerRef,
            searchText2,
            searchRootTenant,
            searchResultsPromise,
            gotoSearchResult,
            searchResultUrl,
            searchTextSet,
            setSearchText2,
            clearSearch,
            close,
        };
    },
});
</script>
